/**
 * Run all boot functions in /boot by boot array
 * env: process.env.VUE_APP_BOOT_PATH
 */
import {App, Boot, Context} from '@/project'
import {State} from '@/store'

export interface BootInfo {
  path: string | Boot,
  options?: any,
}

import Vue from 'vue'

/**
 * Get Boot files
 * @param boots
 */
export const getBoots =
  async <APP extends App = App, STATE = State, VUE extends typeof Vue = typeof Vue>
  (boots: Array<BootInfo | string | Boot>): Promise<any[]> =>
{
  const bootPromises: Array<Promise<any> | any> = boots.reduce(
    (result: Array<Promise<any> | any>, boot: BootInfo | string | Boot) => {
      const load = (path: string) => {
        return  import(
          /* webpackMode: "eager" */
          `@/${process.env.VUE_APP_BOOT_PATH}/${path}`)
      }
      if(typeof boot === 'string') {
        result.push(load(boot))
      } else if(typeof boot === 'function') {
        result.push(boot)
      } else if(typeof boot.path === 'function') {
        result.push(boot.path)
      } else {
        result.push(load(boot.path))
      }

      return result
    },
    [],
  )
  const bootModules = await Promise.all(bootPromises)
  return bootModules.map((module) => {
    return module.default || /* istanbul ignore next: no way to test (can be es module)*/ module
  })
}

/**
 * read all boots
 * @param context
 * @param boot
 */
export default
async <APP extends App = App, STATE = State, VUE extends typeof Vue = typeof Vue>
(context: Context<APP, STATE, VUE>, boot: Array<BootInfo | string | Boot>):
  Promise<Context<APP, STATE, VUE>> =>
{
  const bootFactories: Array<Boot<APP, STATE, VUE>>
    = await getBoots<APP, STATE, VUE>(boot)
  
  const bootRunPromises: Array<void | Promise<void>> = []
  bootFactories.forEach(
    (bootFactory: Boot<APP, STATE, VUE>, index: number) => {
      const _boot = boot[index]
      const options = (typeof _boot === 'string' || typeof  _boot === 'function') ?
        {} : _boot.options
      bootRunPromises.push(bootFactory(context, options))
    },
  )
  await Promise.all(bootRunPromises)
  return context
}
